/**
 * Web Christmas analytics (safe props only).
 * Dedupes offering opens and category flips within a browser tab session.
 */

import { capturePostHogEvent } from "@/lib/posthog-client";
import { POSTHOG_EVENTS, POSTHOG_SOURCE } from "@/lib/posthog-events";
import { createWebChristmasAnalyticsDedupe } from "./webChristmasAnalyticsDedupe";

const dedupe = createWebChristmasAnalyticsDedupe();

function base(props?: Record<string, string | number | boolean | null | undefined>) {
  // PostHog web capture props allow string | boolean only — coerce numbers.
  const out: Record<string, string | boolean> = {
    source: POSTHOG_SOURCE,
    menu_category: "christmas",
  };
  if (!props) return out;
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || v === null || v === "") continue;
    out[k] = typeof v === "number" ? String(v) : v;
  }
  return out;
}

export function captureChristmasWebOfferingOpened(props: {
  offering_id: string;
  restaurant_id?: string | null;
  entry_point?: string;
}): void {
  if (!dedupe.noteOfferingOpened(props.offering_id)) return;
  capturePostHogEvent(
    POSTHOG_EVENTS.CHRISTMAS_WEB_OFFERING_OPENED,
    base({
      offering_id: props.offering_id.trim(),
      restaurant_id: props.restaurant_id,
      entry_point: props.entry_point ?? "direct_link",
    })
  );
}

export function captureChristmasWebCategoryChanged(props: {
  menu_category: "daily" | "christmas";
  restaurant_id?: string | null;
  from_qr?: boolean;
}): void {
  const { fire, previous } = dedupe.noteCategoryChange(props.menu_category);
  if (!fire) return;
  capturePostHogEvent(
    POSTHOG_EVENTS.CHRISTMAS_WEB_CATEGORY_CHANGED,
    base({
      menu_category: props.menu_category,
      previous_category: previous,
      restaurant_id: props.restaurant_id,
      from_qr: props.from_qr === true,
    })
  );
}

/** Fire once per QR-attributed restaurant page session when Christmas UI is shown. */
export function captureChristmasWebQrRestaurantInteraction(props: {
  restaurant_id: string;
  christmas_count: number;
  daily_count: number;
}): void {
  if (!dedupe.noteQrInteraction(props.christmas_count)) return;
  capturePostHogEvent(
    POSTHOG_EVENTS.CHRISTMAS_WEB_QR_RESTAURANT_INTERACTION,
    base({
      restaurant_id: props.restaurant_id,
      christmas_count: props.christmas_count,
      daily_count: props.daily_count,
      entry_point: "qr",
    })
  );
}

export function resetWebChristmasAnalyticsForTests(): void {
  dedupe.reset();
}

/** Test helper — once-guards without requiring a live PostHog client. */
export function webChristmasAnalyticsSnapshotForTests(): {
  openedCount: number;
  lastCategory: "daily" | "christmas" | null;
  qrInteractionFired: boolean;
} {
  return dedupe.snapshot();
}
